import type { FC, MouseEventHandler, ReactNode } from "react";
import { Tooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";
import { cx } from "@/utils/cx";
import { CustomLink } from "../NavItem/CustomLink";

interface NavItemButtonProps {
	/** Custom background class for selected state */
	selectedClassName?: string;
	/** Custom class for the icon */
	iconClassName?: string;
	/** Custom class to apply to the CustomLink wrapper */
	linkClassName?: string;
	/** Override class for hover background (e.g. on-brand alpha hover) */
	hoverClassName?: string;
	/** Whether the collapsible nav item is open. */
	open?: boolean;
	/** URL to navigate to when the nav item is clicked. Optional for parent-only folders. */
	href?: string;
	/** Type of the nav item. */
	type: "link" | "parent" | "child";
	/** Icon component to display. */
	icon?: FC<{ className?: string }>;
	/** Handler for click events. */
	onClick?: MouseEventHandler;
	/** Content to display. */
	children?: ReactNode;
	/** Label text for the tooltip. */
	label: string;
	/** Optional data-testid for nav automation hooks. */
	testId?: string;
}

const ICON_ONLY_BTN_BASE =
	"group relative flex cursor-pointer select-none items-center justify-center rounded-md outline-focus-ring transition duration-100 ease-linear focus-visible:z-10 focus-visible:outline-2 focus-visible:outline-offset-2 size-10 p-2";
const ICON_ONLY_BTN_HOVER = "hover:bg-primary_hover";

export const NavItemButton = ({
	type,
	href,
	icon: Icon,
	children,
	onClick,
	open = false,
	selectedClassName = "bg-active",
	iconClassName = "text-fg-quaternary",
	linkClassName,
	hoverClassName,
	label,
	testId,
}: NavItemButtonProps) => {
	const ICON_ONLY_BTN = cx(
		ICON_ONLY_BTN_BASE,
		hoverClassName ?? ICON_ONLY_BTN_HOVER,
	);
	const iconElement = Icon && (
		<Icon
			aria-hidden="true"
			className={cx("shrink-0 transition-inherit-all size-5", iconClassName)}
		/>
	);

	if (type === "parent") {
		return (
			<Tooltip title={label} placement="right">
				<TooltipTrigger>
					<CustomLink
						to={href}
						activeOptions={{
							exact: false,
							includeSearch: false,
							includeHash: false,
						}}
						className={ICON_ONLY_BTN}
						data-testid={testId}
						inactiveProps={{ className: linkClassName }}
						aria-expanded={open || undefined}
						onClick={onClick}
					>
						{() => (
							<>
								{iconElement}
								<span className={"sr-only"}>{children}</span>
							</>
						)}
					</CustomLink>
				</TooltipTrigger>
			</Tooltip>
		);
	}

	if (type === "child" && href) {
		return (
			<Tooltip title={label} placement="right">
				<TooltipTrigger>
					<CustomLink
						to={href}
						activeOptions={{
							exact: false,
							includeSearch: false,
							includeHash: false,
						}}
						className={ICON_ONLY_BTN}
						data-testid={testId}
						inactiveProps={{ className: linkClassName }}
						activeProps={{
							className: cx(selectedClassName, "hover:bg-secondary_hover"),
							"aria-current": "page",
						}}
						onClick={onClick}
					>
						{() => (
							<>
								{iconElement}
								<span className={"sr-only"}>{children}</span>
							</>
						)}
					</CustomLink>
				</TooltipTrigger>
			</Tooltip>
		);
	}

	if (href) {
		return (
			<Tooltip title={label} placement="right">
				<TooltipTrigger>
					<CustomLink
						to={href}
						activeOptions={{
							exact: false,
							includeSearch: false,
							includeHash: false,
						}}
						className={ICON_ONLY_BTN}
						data-testid={testId}
						inactiveProps={{ className: linkClassName }}
						activeProps={{
							className: cx(selectedClassName, "hover:bg-secondary_hover"),
							"aria-current": "page",
						}}
						onClick={onClick}
					>
						{() => (
							<>
								{iconElement}
								<span className={"sr-only"}>{children}</span>
							</>
						)}
					</CustomLink>
				</TooltipTrigger>
			</Tooltip>
		);
	}

	return null;
};
